// components/quick-capture.js
// ═══════════════════════════════════════════════════════════
// Floating idea capture button. Always visible, any feature.
// Owns: capture UI, open/close state
// Never owns: where the idea goes (calls onSave prop)
// Default destination: Ideas feature
// Future props: destination, onVoiceCapture
// ═══════════════════════════════════════════════════════════
import { useState, useRef, useEffect } from 'react';

export default function QuickCapture({ onSave, disabled = false, controlled = false, open: controlledOpen, onClose }) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlled ? controlledOpen : internalOpen;
  const setOpen = controlled ? (v) => { if (!v) onClose?.(); } : setInternalOpen;
  const [text, setText] = useState('');
  const [destination, setDestination] = useState('ideas');
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  function handleSave() {
    const trimmed = text.trim();
    if (!trimmed) return;
    onSave?.({ content: trimmed, destination });
    setText('');
    setOpen(false);
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    }
    if (e.key === 'Escape') {
      setOpen(false);
      setText('');
    }
  }

  return (
    <div style={{
      position: 'fixed',
      bottom: controlled ? 60 : 32,
      left: controlled ? 354 : 'auto',
      right: controlled ? 'auto' : 32,
      zIndex: 300,
      width: controlled ? 280 : 'auto',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      gap: 12,
    }}>

      {/* Capture panel */}
      {open && (
        <>
          <div
            onClick={() => { setOpen(false); setText(''); }}
            style={{ position: 'fixed', inset: 0, zIndex: 199 }}
          />
          <div style={{
            position: 'relative',
            zIndex: 201,
            width: 300,
            background: '#141414',
            border: '1px solid #2a2a2a',
            borderRadius: 16,
            padding: 16,
            boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
          }}>

            {/* Header */}
            <div style={{
              display: 'flex', alignItems: 'center',
              justifyContent: 'space-between', marginBottom: 12,
            }}>
              <span style={{
                color: '#c8a96e', fontSize: 11,
                letterSpacing: '0.12em', textTransform: 'uppercase',
                fontFamily: 'Georgia, serif',
              }}>
                Quick Capture
              </span>
              <button
                onClick={() => { setOpen(false); setText(''); }}
                style={{
                  background: 'none', border: 'none',
                  color: '#555', cursor: 'pointer',
                  fontSize: 18, padding: 0, lineHeight: 1,
                }}
                onMouseEnter={e => { e.currentTarget.style.color = '#888'; }}
                onMouseLeave={e => { e.currentTarget.style.color = '#555'; }}
              >
                ×
              </button>
            </div>

            {/* Input */}
            <div
              ref={inputRef}
              contentEditable
              suppressContentEditableWarning
              onInput={e => setText(e.currentTarget.innerText)}
              onKeyDown={handleKeyDown}
              data-placeholder="Catch it before it disappears..."
              style={{
                background: '#07070a',
                border: '1px solid #222',
                borderRadius: 10,
                padding: 12,
                marginBottom: 12,
                minHeight: 80,
                color: '#d4d4d4',
                fontSize: 13,
                lineHeight: 1.6,
                fontFamily: 'Georgia, serif',
                outline: 'none',
                whiteSpace: 'pre-wrap',
              }}
            />

            {/* Footer */}
            <div style={{
              display: 'flex', alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <span
                  onClick={() => setDestination('ideas')}
                  style={{
                    color: destination === 'ideas' ? '#7a9e82' : '#555',
                    fontSize: 11, fontStyle: 'italic',
                    fontFamily: 'Georgia, serif', cursor: 'pointer',
                    transition: 'color 0.15s',
                  }}
                >
                  → Ideas
                </span>
                <span style={{ color: '#333', fontSize: 11 }}>·</span>
                <span
                  onClick={() => setDestination('notes')}
                  style={{
                    color: destination === 'notes' ? '#7a9e82' : '#555',
                    fontSize: 11, fontStyle: 'italic',
                    fontFamily: 'Georgia, serif', cursor: 'pointer',
                    transition: 'color 0.15s',
                  }}
                >
                  → Notes
                </span>
              </div>
              <button
                onClick={handleSave}
                disabled={!text.trim()}
                style={{
                  background: text.trim() ? 'rgba(122,158,130,0.1)' : 'none',
                  border: `1px solid ${text.trim() ? 'rgba(122,158,130,0.25)' : '#222'}`,
                  borderRadius: 20,
                  color: text.trim() ? '#7a9e82' : '#444',
                  fontSize: 12,
                  fontFamily: 'Georgia, serif',
                  padding: '5px 14px',
                  cursor: text.trim() ? 'pointer' : 'default',
                  transition: 'all 0.15s',
                }}
              >
                Save
              </button>
            </div>
          </div>
        </>
      )}

      {/* Floating trigger button — hidden in controlled mode */}
      {!controlled && (
        <button
          onClick={() => setOpen(v => !v)}
          disabled={disabled}
          style={{
            width: 44, height: 44,
            borderRadius: '50%',
            background: open ? 'rgba(122,158,130,0.2)' : 'rgba(122,158,130,0.12)',
            border: `1px solid ${open ? 'rgba(122,158,130,0.5)' : 'rgba(122,158,130,0.3)'}`,
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.15s',
            zIndex: 201,
          }}
          onMouseEnter={e => { if (!open) e.currentTarget.style.background = 'rgba(122,158,130,0.2)'; }}
          onMouseLeave={e => { if (!open) e.currentTarget.style.background = 'rgba(122,158,130,0.12)'; }}
        >
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
            <path d="M10 2.5C7.24 2.5 5 4.74 5 7.5c0 1.8.92 3.38 2.32 4.32.4.27.68.7.68 1.18v1a1 1 0 001 1h2a1 1 0 001-1v-1c0-.48.28-.91.68-1.18C14.08 10.88 15 9.3 15 7.5c0-2.76-2.24-5-5-5z" stroke="#7a9e82" strokeWidth="1.4" strokeLinejoin="round"/>
            <path d="M7.5 17h5" stroke="#7a9e82" strokeWidth="1.4" strokeLinecap="round"/>
          </svg>
        </button>
      )}

      <style jsx>{`
        [data-placeholder]:empty::before {
          content: attr(data-placeholder);
          color: #444;
          font-style: italic;
        }
      `}</style>
    </div>
  );
}